import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { useGetList, useNotify } from "ra-core";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { useMemberCapability } from "@/components/atomic-crm/providers/commons/useMemberCapability";
import type { Company, Contact } from "@/components/atomic-crm/types";
import { Switch } from "@/components/ui/switch";
import { RelatedSection } from "@/modules/shared/record/RecordFieldKit";
import {
  portalAccessAllowed,
  resolvePortalAccessReason,
  type PortalAccessReason,
} from "../../../supabase/functions/_shared/portalAccessRule.ts";

const AUTOMATIC_REASON: Partial<Record<PortalAccessReason, string>> = {
  no_company: "No account: signs in with their email or phone.",
  only_contact: "Only person on the account: signs in directly.",
  primary_contact: "Primary contact: always has access.",
  billing_contact: "Billing contact: signs in with their email or phone.",
  invoice_recipient: "Receives invoices: signs in with their email or phone.",
};

/**
 * Who may sign in to the company's single client portal. Only people of a
 * multi-contact account need the switch (rule: portalAccessRule.ts).
 */
export const ContactPortalAccessSection = ({
  record,
  company,
}: {
  record: Contact;
  company?: Company | null;
}) => {
  const canEdit = useMemberCapability("crm.pipeline.edit");
  const notify = useNotify();
  const queryClient = useQueryClient();
  const queryKey = ["contact-portal-access", record.id];

  const { total: companyContactCount = 0 } = useGetList<Contact>(
    "contacts",
    {
      filter: { "company_id@eq": record.company_id ?? -1 },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: record.company_id != null, staleTime: 30_000 },
  );

  const { total: invoiceCount = 0 } = useGetList(
    "client_invoices",
    {
      filter: { "contact_id@eq": record.id, "status@neq": "draft" },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "DESC" },
    },
    { enabled: record.company_id != null, staleTime: 60_000 },
  );

  // Not in contacts_summary: read the column from the table.
  const { data: portalAccess = false } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contacts")
        .select("portal_access")
        .eq("id", record.id)
        .single();
      if (error) throw error;
      return Boolean(data?.portal_access);
    },
    staleTime: 30_000,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (next: boolean) => {
      const { error } = await supabase
        .from("contacts")
        .update({ portal_access: next })
        .eq("id", record.id);
      if (error) throw error;
      return next;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
      notify(next ? "Portal access granted" : "Portal access removed", {
        type: "info",
      });
    },
    onError: () => notify("Could not update portal access", { type: "error" }),
  });

  const reason = resolvePortalAccessReason({
    hasCompany: record.company_id != null,
    isPrimaryContact:
      company?.primary_contact_id != null &&
      String(company.primary_contact_id) === String(record.id),
    companyContactCount,
    isBillingContact: (company?.billing_contact_ids ?? []).some(
      (id) => String(id) === String(record.id),
    ),
    isInvoiceRecipient: invoiceCount > 0,
    portalAccess,
  });
  const automatic = AUTOMATIC_REASON[reason];

  return (
    <RelatedSection title="Client portal">
      {automatic ? (
        <p className="flex items-start gap-2 px-1.5 text-sm text-muted-foreground">
          <CheckCircle2
            className="mt-0.5 size-4 shrink-0 text-success-text"
            aria-hidden
          />
          {automatic}
        </p>
      ) : (
        <label
          htmlFor={`portal-access-${record.id}`}
          className="flex items-start justify-between gap-3 px-1.5"
        >
          <span className="space-y-0.5">
            <span className="block text-sm font-medium">Portal access</span>
            <span className="block text-xs text-muted-foreground">
              {portalAccessAllowed(reason)
                ? `Signs in to ${company?.name ?? "the account"}'s portal with their email or phone.`
                : `Turn on to let this person into ${company?.name ?? "the account"}'s portal.`}
            </span>
          </span>
          <Switch
            id={`portal-access-${record.id}`}
            checked={portalAccess}
            disabled={!canEdit || isPending}
            onCheckedChange={(next) => mutate(next)}
          />
        </label>
      )}
    </RelatedSection>
  );
};
